import {
  clearRaceDebugSequence,
  setRaceDebugSink,
  type RaceDebugSnapshot,
} from '@multirx/shared/game/raceDebugLog.js';
import { API_BASE } from '../config.js';

declare global {
  interface Window {
    __multirxRaceDebugLog?: RaceDebugSnapshot[];
  }
}

const MAX_BUFFER = 8000;
const shipQueue: RaceDebugSnapshot[] = [];
let shipTimer: ReturnType<typeof setTimeout> | null = null;
let shipInFlight: Promise<void> | null = null;

function raceDebugApi(path: string): string {
  return `${API_BASE}/api/dev/race-debug${path}`;
}

function shouldEnableClientRaceDebug(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof localStorage !== 'undefined' && localStorage.getItem('multirx:raceDebug') === '0') {
    return false;
  }
  return true;
}

async function postRaceDebugEntries(entries: RaceDebugSnapshot[]): Promise<void> {
  if (entries.length === 0) return;
  const res = await fetch(raceDebugApi(''), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entries }),
    keepalive: entries.length > 0,
  });
  if (!res.ok) {
    throw new Error(`race-debug POST ${res.status}`);
  }
}

/** Envoie immédiatement la file d'attente au serveur → logs/race-debug.jsonl */
export async function flushClientRaceDebugLogNow(): Promise<void> {
  if (!shouldEnableClientRaceDebug()) return;
  if (shipTimer != null) {
    clearTimeout(shipTimer);
    shipTimer = null;
  }
  if (shipInFlight) {
    await shipInFlight;
  }
  if (shipQueue.length === 0) return;
  const batch = shipQueue.splice(0, shipQueue.length);
  shipInFlight = postRaceDebugEntries(batch)
    .catch(() => {
      // Best-effort : on ne remet pas le lot en file (le tampon local sert de secours),
      // sinon un lot trop volumineux (413) boucle indéfiniment en grossissant à chaque échec.
    })
    .finally(() => {
      shipInFlight = null;
    });

  await shipInFlight;
}

function flushShipQueue(): void {
  shipTimer = null;
  void flushClientRaceDebugLogNow();
}

function shipSnapshotToServer(snapshot: RaceDebugSnapshot): void {
  shipQueue.push(snapshot);
  if (shipTimer != null) return;
  shipTimer = setTimeout(flushShipQueue, 80);
}

/** Buffer local (secours si le streaming HTTP échoue). */
export function getClientRaceDebugBuffer(): readonly RaceDebugSnapshot[] {
  return typeof window !== 'undefined' && window.__multirxRaceDebugLog
    ? window.__multirxRaceDebugLog
    : [];
}

export function clearClientRaceDebugLogOnServer(): Promise<void> {
  if (!shouldEnableClientRaceDebug()) return Promise.resolve();
  shipQueue.length = 0;
  if (typeof window !== 'undefined' && window.__multirxRaceDebugLog) {
    window.__multirxRaceDebugLog.length = 0;
  }
  clearRaceDebugSequence();
  return fetch(raceDebugApi('/clear'), { method: 'POST' })
    .then(() => undefined)
    .catch(() => undefined);
}

export function setupClientRaceDebug(): void {
  if (!shouldEnableClientRaceDebug()) {
    setRaceDebugSink(null);
    return;
  }

  const buffer: RaceDebugSnapshot[] = [];
  clearRaceDebugSequence();

  setRaceDebugSink((snapshot) => {
    buffer.push(snapshot);
    if (buffer.length > MAX_BUFFER) buffer.shift();
    shipSnapshotToServer(snapshot);
    const isMismatch = snapshot.event === 'RANK_VS_TRACK' || snapshot.rankVsTrackOk === false;
    // Ne jamais dumper le snapshot entier dans la console : DevTools + ~1k
    // objets lourds plantent l’onglet (surtout à la rafale de fin de course).
    if (isMismatch) {
      console.warn(`[RACE-DBG #${snapshot.seq}] ${snapshot.event} ⚠ rank≠track`, {
        diceRound: snapshot.diceRound,
        trackOrder: snapshot.trackOrder,
        ranking: snapshot.ranking.map((r) => `P${r.i}`),
      });
    } else if (snapshot.event === 'RACE_FINISHED' || snapshot.event === 'TURN_COMPLETE') {
      console.log(
        `%c[RACE-DBG #${snapshot.seq}] ${snapshot.event}`,
        'color:#2ed573;font-weight:bold',
        snapshot.extra ?? ''
      );
    }
  });

  if (typeof window !== 'undefined') {
    window.__multirxRaceDebugLog = buffer;
    window.addEventListener('pagehide', () => {
      void flushClientRaceDebugLogNow();
    });
    console.info(
      '[RACE-DBG] Logs course → logs/race-debug.jsonl + logs/race-turn-debug.log (vidés à chaque départ)'
    );
  }
}
